import { useContext, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { widgetConfig } from "../components/Editor/editor_config";
import { SVG } from "../constants/editor";
import { EditorContext } from "../containers/editor/EditorLayout";
import { fetchUploadAssets, uploadAssetFile } from "../store/actions/editorActions";
import { getFileType, getImgDimensionFromUrl, getSvgContentFromUrl, RGBToHex } from "../_helpers/utils";
import useAddWidget from "./useAddWidget";
import useAlignment from "./useAlignment";
import useElementInnerHtml from "./useElementInnerHtml";
import { EditorPanelContext } from "../containers/editor/EditorPanel";

const useUploadInsertOutsideImage = () => {
  let { metadata, setisUploadDone, isUploadDone, uploadState, setUploadState, setUploadAssetError } =
    useContext(EditorContext);
  let { keyBoardEvent, updateKeyBoardEvent } = useContext(EditorPanelContext);

  const dispatch = useDispatch();

  // used global states
  const { progressInfo, availableUploadImages, uploadLocation } = useSelector(state => state.editor);

  // Local states
  const [assetType, setAssetType] = useState();
  const [assetInnerContent, setAssetInnerContent] = useState();
  const [widgetWidth, setWidgetWidth] = useState();
  const [widgetHeight, setWidgetHeight] = useState();
  const [dataparam, setDataParam] = useState();
  const [addWidgetClicked, setAddWidgetClicked] = useState(false);

  // used custom Hooks
  const { postion: getPosition } = useAlignment(addWidgetClicked, widgetWidth, widgetHeight, "middle-center");
  const getNewWidgetObject = useAddWidget(
    addWidgetClicked,
    assetType,
    dataparam,
    getPosition,
    widgetWidth,
    widgetHeight
  );
  useElementInnerHtml(
    addWidgetClicked,
    setAddWidgetClicked,
    assetType,
    assetInnerContent,
    dataparam,
    getNewWidgetObject
  );

  const uploadOutsideImage = file => {
    // Return if an upload is already in progress
    if (!isUploadDone) {
      return;
    }

    setAssetType("uploads");

    let fileSize = file.size / 1e6;
    let fileFormat = file.type.split("/");
    let allowedImageTypes = ["jpeg", "jpg", "png", "webp"];

    // validation for file size
    if (fileSize > 5) {
      setTimeout(() => {
        setUploadAssetError({
          status: true,
          message: `File Size should be maximum 5 MB.`,
        });
      }, 100);
    }
    // validation for file format
    else if (!allowedImageTypes.includes(fileFormat[1]?.toLowerCase())) {
      setTimeout(() => {
        setUploadAssetError({
          status: true,
          message: "Invalid Format!!",
        });
      }, 100);
    }
    // upload files
    else {
      const formData = new FormData();
      formData.append("file", file, file.name ? file.name : "png");

      dispatch(fetchUploadAssets({ limit: 25, pageNumber: 1, file_type: "image" })).then(resp => {
        dispatch(uploadAssetFile(formData, "image", "clipboard", resp.data.data.assets));
        setUploadState({ ...uploadState, uploadSchemeType: "Images" });

        setisUploadDone(false);
        if (document.getElementById("imageListWrapper")) document.getElementById("imageListWrapper").scrollTop = 0;
        updateKeyBoardEvent({ ...keyBoardEvent, paste: false });
      });
    }

    setTimeout(function () {
      setUploadAssetError({ status: false, message: "" });
    }, 3000);
  };

  const insertUploadedImage = async ({ url, filename }) => {
    if (!assetType) return;

    let innerContent;

    if (getFileType(filename) !== SVG) {
      innerContent = url;

      const { width: imgWidth, height: imgHeight } = await getImgDimensionFromUrl(url);
      const imgAspectRatio = imgWidth / imgHeight;
      const { width: activeBlockWidth } = document.getElementById(`${metadata.activeBlockId}`)?.getBoundingClientRect();
      const width = imgWidth <= activeBlockWidth ? imgWidth : activeBlockWidth;
      const height = imgWidth <= activeBlockWidth ? imgHeight : width / imgAspectRatio;

      setWidgetWidth(width);
      setWidgetHeight(height);
    } else {
      innerContent = await getSvgContentFromUrl(url);

      //Update multicolor uploaded svg innerhtml with data attribute for different color path after insertion
      innerContent.querySelectorAll(`svg *`).forEach(node => {
        if (
          node.nodeName === "defs" ||
          node.parentNode.nodeName === "defs" ||
          node.parentNode.nodeName === "linearGradient"
        )
          return false;

        if (node.nodeName !== "g" && node.style && RGBToHex(node.style.fill) !== "") {
          const currentIconColor = RGBToHex(node.style.fill);
          node.dataset.class = `${currentIconColor}body`;
        }
      });

      setWidgetWidth(innerContent?.viewBox?.baseVal?.width ?? widgetConfig[props.assetType].width);
      setWidgetHeight(innerContent?.viewBox?.baseVal?.height ?? widgetConfig[props.assetType].height);
    }

    setDataParam({ ...widgetConfig[assetType].dataAttr, "data-file-type": getFileType(filename) });
    setAssetInnerContent(innerContent);
    setAddWidgetClicked(true);
    setTimeout(() => {
      setAssetType();
    }, 1000);
  };

  useEffect(() => {
    if (progressInfo === 100 && uploadLocation === "clipboard") {
      const lastUploadedImageFromClipboard = availableUploadImages[0];

      insertUploadedImage(lastUploadedImageFromClipboard);

      setTimeout(() => {
        setisUploadDone(true);
        setUploadState({ ...uploadState, uploadSchemeType: null });
      }, 1000);
    }
  }, [progressInfo]);

  return { uploadOutsideImage };
};

export default useUploadInsertOutsideImage;
